/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 *
 * 宿泊税コンポーネント
 *
 * ※注意: このコンポーネントは2018年10月から施行された京都市の条例のみに対応しています
 *
 */
import '../AccomodationTax.scss'
import React, { useEffect, useReducer, useState } from 'react'
import { CarefulButton, Dialog, DialogContent, DialogTitle, NormalButton, OutlinedButton } from 'ui-library/dist/external/components'
import f from '../../../../util/NumberFormat'
import r from '../../../../util/Renderer'
import PlanSimpleDisplay from './component/PlanSimpleDisplay'
import * as Action from './Action'
import { accommodationTaxInitialState } from './State'
import { reducer } from './Reducer'
import TaxCalculator from '../../../../domain/model/tax/AccommodationTax'
import HotelRepository from '../../../../infra/HotelRepository'

function AccommodationTax(props) {
  useEffect(() => {
    // プラン取得
    const findPlan = async (hotelId, planCode) => {
      let plan = null
      if (planCode) {
        const result = await HotelRepository.findPlan(hotelId, planCode)
        plan = result.plan
      }
      dispatch({
        type: Action.LOAD_PLAN,
        payload: {
          hotelId,
          planCode,
          plan: plan
        }
      })
    }
    if (props.reservation_id) {
      findPlan(props.hotel_id, props.plan_code)
      dispatch({
        type: Action.INIT,
        payload: {
          hotel_id: props.hotel_id,
          plan_code: props.plan_code,
          booking_price: props.booking_price,
          people: props.people,
          stay: props.stay,
          site_referer: props.site_referer,
          accommodation_tax: props.accommodation_tax
        }
      })
    }
  }, [props.reservation_id])

  const [state, dispatch] = useReducer(reducer, accommodationTaxInitialState)
  const [dialog, toggleDialog] = useState(false)
  const action = (type, payload) => dispatch({ type: type, payload: payload })

  return (
    <div className={`AccomodationTaxButton ${props.className || ''}`}>
      <Dialog
        className="AccomodationTaxDialog"
        open={dialog}
        onClose={() => {
          toggleDialog(false)
          props.onClosed()
        }}
      >
        <DialogTitle>宿泊税の計算</DialogTitle>
        <DialogContent>
          <table>
            <tbody>
              <tr>
                <th style={{ textAlign: 'left' }}>予約料金</th>
                <td style={{ textAlign: 'right' }}>
                  <input type="number" style={{ width: '70px' }} value={state.booking_price || 0} onChange={e => action(Action.EDIT, { name: 'booking_price', value: e.target.value })} /> 円
                </td>
              </tr>
              <tr>
                <th style={{ textAlign: 'left' }}>宿泊数</th>
                <td>
                  <input type="number" min="1" max="100" value={state.stay} onChange={e => action(Action.EDIT, { name: 'stay', value: e.target.value })} /> 泊
                </td>
              </tr>
              <tr>
                <th style={{ textAlign: 'left' }}>人数</th>
                <td>
                  <input type="number" min="1" max="100" value={state.people} onChange={e => action(Action.EDIT, { name: 'people', value: e.target.value })} /> 人
                </td>
              </tr>

              <tr>
                <th style={{ textAlign: 'left' }}>OTA手数料</th>
                <td>
                  <input
                    id="AccomodationTaxButton__include_commission"
                    type="checkbox"
                    checked={state.include_commission}
                    onChange={e => action(Action.EDIT, { name: 'include_commission', value: e.target.checked })}
                  />
                  &nbsp;
                  <label htmlFor="AccomodationTaxButton__include_commission">含める</label>
                </td>
              </tr>
              <tr>
                <th>&nbsp;</th>
                <td>
                  <input type="number" min="0" max="100" value={state.commission_rate} onChange={e => action(Action.EDIT, { name: 'commission_rate', value: e.target.value })} /> %
                </td>
              </tr>

              <tr>
                <th style={{ textAlign: 'left' }}>予約総額</th>
                <td>{f.commaSeparated(state.total_price)} 円</td>
              </tr>
            </tbody>
          </table>

          {r.renderIf(state.plan)(
            <>
              <hr />
              <PlanSimpleDisplay plan={state.plan} stay={state.stay} people={state.people} total_price={state.total_price} />
            </>
          )}

          <hr />

          <table>
            <tbody>
              <tr>
                <th style={{ textAlign: 'left' }}>
                  <strong>宿泊料金</strong>
                </th>
                <td style={{ textAlign: 'right' }}>
                  <input type="number" style={{ width: '80px' }} value={state.accommodation_fee} onChange={e => action(Action.ACCOMMODATION, { name: 'accommodation_fee', value: e.target.value })} />{' '}
                  円
                </td>
              </tr>

              <tr>
                <th>宿泊税</th>
                <td>
                  <input
                    id="AccomodationTaxButton__isUnCollected"
                    type="checkbox"
                    checked={state.isUncollected}
                    onChange={e => action(Action.UNCOLLECTED, { name: 'isUncollected', value: e.target.checked })}
                  />
                  &nbsp;
                  <label htmlFor="AccomodationTaxButton__isUnCollected">未回収</label>
                </td>
              </tr>
              <tr>
                <th>
                  <small>宿泊</small>
                </th>
                <td style={{ textAlign: 'right' }}>
                  {f.commaSeparated(getAccommodationPricePerPerson(state.accommodation_fee, state.people, state.stay))} 円 <small>(1泊1名)</small>
                </td>
              </tr>
              <tr>
                <th>
                  <small>税</small>
                </th>
                <td style={{ textAlign: 'right' }}>
                  {f.commaSeparated(state.tax_price)} 円 <small>(1泊1名)</small>
                </td>
              </tr>
              <tr>
                <th>徴収額</th>
                <td>
                  <strong>{f.commaSeparated(state.total_tax_price)} 円</strong>
                </td>
              </tr>
              <tr>
                <th>メモ</th>
                <td>
                  <textarea value={state.memo} onChange={e => action(Action.MEMO, { value: e.target.value })} style={{ width: '100px' }} />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="AccomodationTaxDialog__Buttons">
            {r.renderIf(state.id)(
              <CarefulButton
                onClick={() => {
                  if (window.confirm('宿泊税を削除すると宿泊税未払いに戻ります。よろしいですか？')) {
                    props.onDelete(state)
                    toggleDialog(false)
                    props.onClosed()
                  }
                }}
              >
                削除
              </CarefulButton>
            )}
            <OutlinedButton
              onClick={() => {
                props.onSave(state)
                toggleDialog(false)
                props.onClosed()
              }}
            >
              宿泊税を受領
            </OutlinedButton>
          </div>
        </DialogContent>
      </Dialog>
      <NormalButton
        onClick={() => {
          toggleDialog(true)
          props.onOpened()
        }}
      >
        {props.children}
      </NormalButton>
    </div>
  )
}

function getAccommodationPricePerPerson(accommodation_price, people, stay) {
  if (!accommodation_price || !people || !stay) {
    return 0
  }

  return Math.floor(accommodation_price / people / stay)
}

export default AccommodationTax
